import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import FocusTrap from "focus-trap-react";
import { FaTimes } from "react-icons/fa";
import SearchBox from "../../../common/SearchBox/SearchBox";
import { Pagination, Loading } from "../../../common";
import "./MfaUsersModal.css";
import { TYPE_COLORS } from "../../../../utils/constants";
import { getInitials } from "../../../../utils/helpers";
import { useTimezone } from "../../../../context/TimezoneContext";
import { formatDateByTimezone } from "../../../../utils/dateFormatter";
import { useTheme } from "../../../../context/ThemeContext";

/**
 * Format method name
 * Example: authenticator_push → Authenticator Push
 */
const formatMethod = (method = "") => {
  return method
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Get strength class
 */
const getStrengthClass = (strength = "") => {
  const s = strength.toLowerCase();
  if (s.includes("strong")) return "strong";
  if (s.includes("medium")) return "medium";
  return "weak";
};

/**
 * Create/get modal root outside #app-content
 */
// const getModalRoot = () => {
//   let modalRoot = document.getElementById("modal-root");

//   if (!modalRoot) {
//     modalRoot = document.createElement("div");
//     modalRoot.id = "modal-root";
//     document.body.appendChild(modalRoot);
//   }

//   return modalRoot;
// };

/**
 * MFA Users Modal Component
 */
const MfaUsersModal = ({
  isOpen,
  onClose,
  method,
  mfaData,
  loading = false,
  pagination,
  onPageChange,
  onPageSizeChange,
  onSearch,
  searchValue = "",
  closeOnOverlay = true,
  closeOnEscape = true,
}) => {
  const users = mfaData?.users || [];
  const { timezone } = useTimezone();
  const modalRef = useRef(null);
  const { isDark } = useTheme();

  useEffect(() => {
    if (!isOpen) return;

    const appContent = document.getElementById("app-content");
    const previousOverflow = document.body.style.overflow;
    const previousFocusedElement = document.activeElement;

    /**
     * Disable background interaction and keyboard tab focus.
     * Important: modal must be rendered outside #app-content using portal.
     */
    appContent?.setAttribute("inert", "");
    appContent?.setAttribute("aria-hidden", "true");

    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (closeOnEscape && e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      appContent?.removeAttribute("inert");
      appContent?.removeAttribute("aria-hidden");

      document.body.style.overflow = previousOverflow;

      document.removeEventListener("keydown", handleEscape);

      if (previousFocusedElement?.focus) {
        previousFocusedElement.focus();
      }
    };
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) {
      onClose();
    }
  };

  const hasUsers = users.length > 0;
  // const modalRoot = getModalRoot();

  return createPortal(
    <div className={`modal-overlay ${isDark ? 'dark dark-theme' : 'light light-theme'}`} onClick={handleOverlayClick}>
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          initialFocus: () =>
            modalRef.current?.querySelector(".modal-close") || modalRef.current,
          fallbackFocus: () => modalRef.current,
          returnFocusOnDeactivate: true,
          escapeDeactivates: false,
          clickOutsideDeactivates: false,
          allowOutsideClick: true,
        }}
      >
        <div
          ref={modalRef}
          className="modal modal-lg"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mfa-modal-title"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ──────────────────────────── */}
          <div className="mfa-modal-header">
            <div className="mfa-modal-header-content">
              <h2 id="mfa-modal-title" className="modal-title">
                {formatMethod(method || mfaData?.method)} Users
              </h2>

              <div className="mfa-modal-meta">
                <span className="mfa-modal-count">
                  <strong>
                    {pagination?.totalCount ?? mfaData?.count ?? 0}
                  </strong>{" "}
                  users
                </span>

                {mfaData?.strength && (
                  <span
                    className={`mfa-strength-badge mfa-strength-${getStrengthClass(
                      mfaData.strength
                    )}`}
                  >
                    {mfaData.strength}
                  </span>
                )}

                {mfaData?.percentage !== undefined && (
                  <span className="mfa-modal-percentage">
                    {mfaData.percentage}%
                  </span>
                )}
              </div>
            </div>

            {/* ── Server-side Search ───────────── */}
            <div className="mfa-header-search-wrapper">
              <SearchBox
                value={searchValue}
                onChange={onSearch}
                placeholder="Search user..."
                debounceMs={500}
                showClear={true}
                className="mfa-header-search"
              />

              {searchValue && (
                <div className="mfa-header-search-results">
                  {pagination?.totalCount ?? 0} results
                </div>
              )}
            </div>

            <button
              type="button"
              className="modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <FaTimes />
            </button>
          </div>

          {/* ── Body ────────────────────────────── */}
          <div className="modal-body">
            {loading ? (
              <Loading text="Loading users…" />
            ) : hasUsers ? (
              <>
                <div
                  className="identity-table-container"
                  role="region"
                  aria-label="MFA Users table"
                >
                  <table className="identity-table" role="table">
                    <thead>
                      <tr>
                        <th scope="col" className="th-identity">
                          Identity
                        </th>
                        <th scope="col" className="th-department">
                          Job Title
                        </th>
                        <th scope="col" className="th-status">
                          Last Sign-In
                        </th>
                        <th scope="col" className="th-status">
                          Password Changed
                        </th>
                        <th scope="col" className="th-status">
                          Created At
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {users.map((user, idx) => {
                        const rowId = user.id || idx;
                        const userType = user.type || "user";

                        return (
                          <tr key={rowId} className="identity-row" role="row">
                            <td className="td-identity">
                              <div className="identity-cell">
                                <div
                                  className="identity-avatar"
                                  style={{
                                    backgroundColor: `${
                                      TYPE_COLORS[userType] || "#999"
                                    }15`,
                                    color: TYPE_COLORS[userType] || "#999",
                                  }}
                                  aria-hidden="true"
                                >
                                  {getInitials(user.name || "NA")}
                                </div>

                                <div className="identity-info">
                                  <div className="identity-name">
                                    {user.name || "Unnamed"}
                                  </div>
                                  <div className="identity-secondary">
                                    {user.upn || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="td-department">
                              <span className="cell-text">
                                {user.jobTitle || "N/A"}
                              </span>
                            </td>

                            <td className="td-status">
                              <span className="cell-text">
                                {user?.lastSignIn
                                  ? formatDateByTimezone(
                                      user.lastSignIn,
                                      timezone,
                                      "Never"
                                    )
                                  : "Never"}
                              </span>
                            </td>

                            <td className="td-status">
                              <span className="cell-text">
                                {user?.lastPasswordChanged
                                  ? formatDateByTimezone(
                                      user.lastPasswordChanged,
                                      timezone,
                                      "N/A"
                                    )
                                  : "N/A"}
                              </span>
                            </td>

                            <td className="td-status">
                              <span className="cell-text">
                                {user?.createdAt
                                  ? formatDateByTimezone(
                                      user.createdAt,
                                      timezone,
                                      "N/A"
                                    )
                                  : "N/A"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {pagination && pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                  />
                )}
              </>
            ) : (
              <div className="usecase-modal-empty">
                {searchValue ? (
                  <p>No users found matching &ldquo;{searchValue}&rdquo;</p>
                ) : (
                  <p>No users found for this MFA method</p>
                )}
              </div>
            )}
          </div>
        </div>
      </FocusTrap>
    </div>,
    document.body
  );
};

export default MfaUsersModal;